import { AuthProvider } from 'react-admin';
import { tokenService, UserIdentity } from './services/tokenService';
import { devLog } from './utils/devLog';

// Module-level flag to prevent concurrent checkAuth/logout calls from racing
let oauthRedirectInProgress = false;

// Shared OAuth redirect logic — used by both login() and checkAuth()
const startOAuthFlow = async () => {
    const codeVerifier = generatePKCE();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const clientId = process.env.REACT_APP_OAUTH_CLIENT_ID || 'frontend-client';
    const ssoUrl = process.env.REACT_APP_SSO_URL || 'https://oa-sso.wsk.gmbh';

    const params = new URLSearchParams({
        response_type: 'code',
        client_id: clientId,
        redirect_uri: window.location.origin + '/auth-callback',
        scope: 'email profile',
        code_challenge: codeChallenge,
        code_challenge_method: 'S256'
    });

    tokenService.setRedirectPath(
        window.location.pathname + window.location.search + window.location.hash
    );
    devLog('[authProvider] Redirecting to OAuth server');
    window.location.href = `${ssoUrl}/authorize?${params}`;
};

const authProvider: AuthProvider = {
    login: async () => {
        devLog('[authProvider] login called');
        tokenService.setUserLoggedOut(false);

        if (tokenService.hasValidToken()) {
            devLog('[authProvider] Token exists, resolving login');
            return Promise.resolve();
        }

        devLog('[authProvider] No token, starting OAuth flow');
        await startOAuthFlow();
        return new Promise(() => {});
    },

    logout: (params?: any) => {
        devLog('[authProvider] logout called', params);

        // If OAuth redirect is already happening, don't interfere
        if (oauthRedirectInProgress) {
            devLog('[authProvider] OAuth redirect in progress, skipping logout');
            return Promise.resolve();
        }

        // Check if auto-login is in progress - don't interfere with the login flow
        if (tokenService.isAutoLoginInProgress()) {
            devLog('[authProvider] Auto-login in progress, not redirecting');
            return Promise.resolve();
        }

        // Check if user was already logged out - prevent infinite redirect loop
        if (tokenService.isUserLoggedOut()) {
            devLog('[authProvider] User already logged out, not redirecting again');
            return Promise.resolve();
        }

        // If there's no token, this is an implicit logout (e.g., checkAuth failed)
        // Mark as logged out but don't redirect — let LoginPage handle it
        if (!tokenService.getToken()) {
            devLog('[authProvider] No token present, implicit logout');
            tokenService.setUserLoggedOut(true);
            return Promise.resolve();
        }

        // Explicit logout with valid token — clear everything and redirect to SSO
        tokenService.clearAll();
        tokenService.setUserLoggedOut(true);

        const ssoUrl = process.env.REACT_APP_SSO_URL || 'https://oa-sso.wsk.gmbh';
        const dashboardUrl = `${ssoUrl}/dashboard`;
        devLog('[authProvider] Explicit logout - redirecting to SSO dashboard');
        window.location.href = dashboardUrl;
        return new Promise(() => {}); // Never resolve
    },

    checkError: (error) => {
        const status = error.status;
        // Only logout on 401 (unauthorized), not on 403 (forbidden)
        if (status === 401) {
            tokenService.removeToken();
            return Promise.reject();
        }
        // 403 means user is authenticated but not authorized - don't logout
        return Promise.resolve();
    },

    checkAuth: () => {
        // If we're already redirecting to SSO, all concurrent checks should wait
        if (oauthRedirectInProgress) {
            return new Promise(() => {});
        }

        if (!tokenService.hasValidToken()) {
            // If auto-login is in progress, redirect to SSO immediately
            // This prevents the checkAuth → logout → checkAuth loop
            if (tokenService.isAutoLoginInProgress()) {
                devLog('[authProvider] checkAuth: No token, auto-login — redirecting to SSO');
                oauthRedirectInProgress = true;
                tokenService.setAutoLoginInProgress(false);
                startOAuthFlow();
                return new Promise(() => {});
            }

            devLog('[authProvider] checkAuth: No valid token');
            tokenService.removeToken();
            return Promise.reject({ message: false });
        }

        tokenService.setAutoLoginInProgress(false);
        return Promise.resolve();
    },

    getPermissions: () => {
        if (!tokenService.getToken()) {
            devLog('[authProvider] getPermissions: No token found');
            return Promise.resolve([]);
        }

        try {
            const combined = tokenService.getAllPermissions();
            devLog('[authProvider] getPermissions: Combined permissions/roles:', combined);
            return Promise.resolve(combined);
        } catch (error) {
            devLog('[authProvider] getPermissions: Error:', error);
            return Promise.resolve([]);
        }
    },

    getIdentity: async () => {
        // hasValidToken statt nur getToken: prüft zusätzlich Ablauf.
        // Ein abgelaufener Token würde sonst zu einem fetch mit 401 führen,
        // im Audit als "Failed to fetch" sichtbar (Cluster E).
        if (!tokenService.hasValidToken()) return Promise.reject();
        const token = tokenService.getToken()!;

        // Check if we have cached identity
        const cachedIdentity = tokenService.getCachedIdentity();
        if (cachedIdentity) {
            return Promise.resolve(cachedIdentity);
        }

        try {
            // Fetch user info from /api/userinfo endpoint to get numeric ID
            const ssoUrl = process.env.REACT_APP_SSO_URL || 'https://oa-sso.wsk.gmbh';
            const response = await fetch(`${ssoUrl}/api/userinfo`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                return Promise.reject();
            }

            const userInfo = await response.json();

            const identity: UserIdentity = {
                id: userInfo.id,
                uuid: userInfo.sub,
                fullName: userInfo.email,
                firstName: userInfo.firstName || userInfo.firstname || userInfo.first_name || '',
                lastName: userInfo.lastName || userInfo.lastname || userInfo.last_name || '',
                email: userInfo.email || '',
                avatar: userInfo.avatarUrl ? `${ssoUrl}${userInfo.avatarUrl}` : undefined,
                companyId: userInfo.companyId || userInfo.company_id || null,
            };

            // Cache the identity
            tokenService.setCachedIdentity(identity);

            return Promise.resolve(identity);
        } catch (error) {
            console.error('[authProvider] getIdentity error:', error);
            return Promise.reject();
        }
    },
};

function generatePKCE(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    const arrayString = Array.from(array, byte => String.fromCharCode(byte)).join('');
    const codeVerifier = btoa(arrayString)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');

    tokenService.setCodeVerifier(codeVerifier);
    return codeVerifier;
}

async function generateCodeChallenge(codeVerifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const hash = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hash), byte => String.fromCharCode(byte)).join('');
    const base64 = btoa(hashArray)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
    return base64;
}

export default authProvider;
